import React from 'react';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import { Button, Colors, Display } from 'Views/Components/Button/Button';

/**
 * Why the payment form could not be shown. Each one was previously an indefinite spinner: the
 * wrapper could not tell "still loading" from "this will never load", so it rendered the loading
 * state for both and the customer waited forever with nothing to act on.
 */
export type PaymentUnavailableReason =
	/** The payment configuration could not be fetched. */
	| 'config'
	/** The amount payable could not be fetched. */
	| 'amount'
	/** The provider's SDK never loaded — almost always a network or extension blocking its CDN. */
	| 'blocked'
	/**
	 * The SDK loaded, but the card element itself refused to mount. Distinct from 'blocked': the
	 * script arrived, so the network is fine and retrying the download would change nothing. This
	 * is a bad publishable key, bad element options, or the provider's API rejecting the element.
	 */
	| 'element'
	/** Nothing failed, but nothing arrived either, and we have waited long enough to say so. */
	| 'stalled'
	/** The amount came back as $0, which is not payable and should never reach this form. */
	| 'zero';

export interface PaymentUnavailableProps {
	reason: PaymentUnavailableReason;
	onRetry?: () => void;
}

/**
 * Kept to a sentence or two each. These render inline above the card fields, not in a dialog with
 * room to spare, and a paragraph there pushes the payment options off the screen.
 */
const COPY: Record<PaymentUnavailableReason, { title: string; body: string; canRetry: boolean }> = {
	config: {
		title: 'Card payment could not be loaded',
		body: 'Check your connection and try again, or choose another payment method.',
		canRetry: true,
	},
	amount: {
		title: 'Card payment could not be loaded',
		body: 'We could not work out the amount payable. Check your connection and try again.',
		canRetry: true,
	},
	blocked: {
		title: 'Your network is blocking card payments',
		body:
			'Something on this network or browser is stopping our payment provider from loading — common '
			+ 'on work and school networks. Try mobile data, or turn off any ad blocker for this site.',
		canRetry: true,
	},
	element: {
		title: 'Card payment could not be loaded',
		body: 'The card form did not load. Try again, or choose another payment method.',
		canRetry: true,
	},
	stalled: {
		title: 'Card payment is taking too long',
		body: 'The payment form has not finished loading. Try again, or choose another payment method.',
		canRetry: true,
	},
	zero: {
		title: 'Nothing to pay',
		body: 'This booking has no amount payable. Go back and try again, and contact us if it persists.',
		canRetry: false,
	},
};

/**
 * The error state the payment form falls back to. Deliberately one component for every reason so
 * that no failure path can be added without also being given something to say.
 */
export function PaymentUnavailable({ reason, onRetry }: PaymentUnavailableProps) {
	const { title, body, canRetry } = COPY[reason];

	return (
		<div className="payment-unavailable">
			<MessageBox
				slim
				status="error"
				title={title}
				actions={
					canRetry && onRetry ? (
						// Outline with no colour class on purpose: MessageBox styles outline buttons in
						// its own status colour, so this picks up the error red. A solid branded button
						// here fought the box's tint instead of sitting inside it.
						<Button display={Display.Outline} colors={Colors.None} newStyles onClick={onRetry}>
							Try again
						</Button>
					) : undefined
				}
			>
				{body}
			</MessageBox>
		</div>
	);
}

/**
 * What the card-fields slot should show in place of the card fields.
 *
 * The card fields are one part of `CheckOutForm`, which also owns `<form id={PAYMENT_FORM_ID}>` —
 * the form cash, EFTPOS, invoice and pay-later all submit through. So a card problem must never be
 * reported by unmounting that form: doing so takes every other payment method down with it, and
 * leaves the pay button targeting a form that is no longer in the document.
 *
 * Passed through context rather than props because the thing that discovers the problem
 * (`PaymentProviderWrapper`, or a provider chunk that renders `children` opaquely) is not the thing
 * that renders the slot.
 */
export type CardFieldsState =
	/** The provider SDK or the amount payable has not arrived yet. */
	| { kind: 'loading' }
	/** It is not coming. `reason` decides the wording. */
	| { kind: 'unavailable'; reason: PaymentUnavailableReason; onRetry?: () => void };

const CardFieldsStateContext = React.createContext<CardFieldsState | null>(null);

export function CardFieldsStateProvider({
	value,
	children,
}: {
	/** Null when the card fields are fine — the slot then renders them as normal. */
	value: CardFieldsState | null;
	children: React.ReactNode;
}) {
	return <CardFieldsStateContext.Provider value={value}>{children}</CardFieldsStateContext.Provider>;
}

/** Null when the card fields are fine and should render normally. */
export function useCardFieldsState(): CardFieldsState | null {
	return React.useContext(CardFieldsStateContext);
}

/**
 * How the card form tells the provider chunk above it that its element would not mount.
 *
 * A second context, pointing the other way, because the two cannot be one. `CardFieldsState` flows
 * DOWN from whoever discovered the problem to the slot that renders it; this flows UP from the card
 * form to the provider chunk. The card form sits *inside* `CardFieldsStateProvider` — it consumes
 * that context, so it cannot also publish to it.
 *
 * Reporting rather than rendering the error itself keeps every failure reason in one place. A form
 * that rendered its own error would be a second thing to keep in step with `COPY`, and would do it
 * inside the slot whose whole job is deciding what the slot shows.
 */
export interface CardFieldsFailureReporter {
	/** The element refused to mount. Safe to call more than once. */
	reportElementFailure: () => void;
}

const CardFieldsFailureReporterContext = React.createContext<CardFieldsFailureReporter | null>(null);

export function CardFieldsFailureReporterProvider({
	value,
	children,
}: {
	value: CardFieldsFailureReporter;
	children: React.ReactNode;
}) {
	return (
		<CardFieldsFailureReporterContext.Provider value={value}>
			{children}
		</CardFieldsFailureReporterContext.Provider>
	);
}

/**
 * Null outside a provider chunk — a card form rendered without one simply has nowhere to report,
 * which is not worth throwing over.
 */
export function useCardFieldsFailureReporter(): CardFieldsFailureReporter | null {
	return React.useContext(CardFieldsFailureReporterContext);
}

/**
 * The more serious of two card-field states, `null` (fine) being the least serious.
 *
 * Two things can independently stop the card fields showing: the provider chunk's own SDK load, and
 * everything the wrapper waits on outside it (payment config, amount payable). Both now report
 * through the SAME context, because neither may unmount the other — so the provider chunk merges
 * the wrapper's state with its own rather than overriding it, and whichever is worse is what the
 * slot shows.
 */
export function mergeCardFieldsState(
	outer: CardFieldsState | null | undefined,
	own: CardFieldsState | null | undefined,
): CardFieldsState | null {
	if (own?.kind === 'unavailable') {
		return own;
	}
	if (outer?.kind === 'unavailable') {
		return outer;
	}
	if (own?.kind === 'loading' || outer?.kind === 'loading') {
		return { kind: 'loading' };
	}
	return null;
}
